import { useCallback, useContext, useEffect, useRef } from 'react';
import { IPortfolioPerformanceAccounts } from '../portfolio-performance.types';
import { WebSocketContext } from '../../websocket-context/websocket-provider';
import { MetricKey, TickPayload } from './risk.types';
import { combineConstituentMetrics } from './risk.helpers';

// A single ticker update carries every symbol's tick in resp.params.data[], so
// combining is a matter of summing the constituent books' metrics at the shared
// timestamp. Returns null until every requested book is present in the update,
// so the combined figure never briefly drops a book. `cashPvOnly` threads the
// same policy flag used by combineConstituentMetrics: when set, a stablecoin
// book adds to PV only (zero to every greek).
export const sumTicks = (
  entries: { symbol: string; metrics: Record<MetricKey, number> }[],
  symbols: string[],
  cashPvOnly: boolean,
): Record<MetricKey, number> | null => {
  const matched = symbols
    .map((s) => entries.find((e) => e.symbol === s))
    .filter((e): e is (typeof entries)[number] => !!e);
  if (matched.length !== symbols.length || matched.length === 0) return null;
  return combineConstituentMetrics(matched, cashPvOnly);
};

export const useAccountTicker = (
  account: IPortfolioPerformanceAccounts | null,
  tokens: string[],
  riskTokenType: string,
  combined: boolean,
) => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);
  const listeners = useRef<Set<(t: TickPayload) => void>>(new Set());
  const keyRef = useRef<string | null>(null);
  const selectedSymbolsRef = useRef<string[]>([]);
  // Read live in the tick handler so combined<->single toggles apply without
  // tearing down and re-establishing the account subscription.
  const combinedRef = useRef(combined);

  const subscribeToWsTicker = (fn: (t: TickPayload) => void) => {
    listeners.current.add(fn);
    return () => {
      listeners.current.delete(fn);
    };
  };

  useEffect(() => {
    selectedSymbolsRef.current = tokens.map((t) => t.replace(/ /g, ''));
  }, [tokens]);

  useEffect(() => {
    combinedRef.current = combined;
  }, [combined]);

  const resubscribe = useCallback(() => {
    if (keyRef.current) {
      unsubscribeFromChannel(keyRef.current);
      keyRef.current = null;
    }

    if (!isConnected || !account || selectedSymbolsRef.current.length === 0) return;

    const key = `PP_TICKER_${account.name}`;
    keyRef.current = key;

    sendMessage(
      JSON.stringify({
        jsonrpc: '2.0',
        id: key,
        method: 'subscribe.portfolio_performance.account_ticker',
        params: { account: account.name, type: riskTokenType },
      }),
    );

    subscribeToChannel(key, (resp: any) => {
      if (resp.method !== 'update' || resp.error) return;
      const entries = resp.params.data as {
        symbol: string;
        timestamp: string;
        metrics: Record<MetricKey, number>;
      }[];
      const metrics = sumTicks(entries, selectedSymbolsRef.current, combinedRef.current);
      if (!metrics) return;
      // All constituents share the update's timestamp; read it off the first.
      const source = entries.find((e) => e.symbol === selectedSymbolsRef.current[0])!;
      const ts = Math.floor(new Date(source.timestamp + 'Z').getTime() / 1000);
      const payload: TickPayload = { timestamp: ts, metrics };
      listeners.current.forEach((fn) => fn(payload));
    });
  }, [
    account,
    riskTokenType,
    isConnected,
    sendMessage,
    subscribeToChannel,
    unsubscribeFromChannel,
  ]);

  useEffect(() => {
    resubscribe();

    return () => {
      if (keyRef.current) unsubscribeFromChannel(keyRef.current);
    };
  }, [resubscribe]);

  return { subscribeToWsTicker, resubscribe };
};
